import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleAccessError, ModuleAccessLoading } from "@/components/ModuleAccessStates";

interface ModuleAccess {
  /** The user may see the module now. Pass it as `enabled` to the module's queries. */
  canView: boolean;
  /** Return this instead of the page when it isn't null. */
  gate: ReactElement | null;
}

/**
 * Page-level guard for a feature-flagged admin module (`public.feature_flags`).
 * Call it first, with the page's other hooks, then `if (gate) return gate;` after the last one.
 *
 * - Super admins bypass the flag, so they can check a module before rollout.
 * - Nobody is redirected while flags load: featureFlags is {} until then, and
 *   reading that as "off" would bounce a flag-on user who reloads (#627).
 * - A failed flag fetch shows an error with a Reload button rather than a
 *   redirect, since the map is {} then too.
 * - canView stays false until access is decided, so the module's queries don't
 *   fire for flag-off users or while flags load (#649).
 */
export function useModuleAccess(moduleKey: string, label: string): ModuleAccess {
  const { featureFlags, featureFlagsStatus, isSuperAdmin } = useAuth();
  const canView = isSuperAdmin || (featureFlagsStatus === "loaded" && Boolean(featureFlags[moduleKey]));

  let gate: ReactElement | null = null;
  if (!isSuperAdmin) {
    if (featureFlagsStatus === "loading") gate = <ModuleAccessLoading />;
    else if (featureFlagsStatus === "error") gate = <ModuleAccessError label={label} />;
    else if (!featureFlags[moduleKey]) gate = <Navigate to="/admin" replace />;
  }

  return { canView, gate };
}
